import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { FilterGroup, FilterSelect, SearchBar, GalleryCard, Tabs, TabsList, TabsTrigger, ListHeader, Pagination, PageHeader, EmptyState, Button } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useScenarioGalleryList } from '@/hooks/useScenarioGalleryList';
import type { ScenarioGalleryItem } from '@/types/scenarioGallery';

export default function ScenarioList(): React.ReactElement {
  const { t } = useTranslation('scenario');
  const { data: allScenarios = [], isLoading, isError, error, refetch } = useScenarioGalleryList();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [classFilter] = useState<string>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [categoryTab, setCategoryTab] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 12;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, platformFilter, categoryTab, allScenarios.length]);

  // 필터링된 시나리오
  const filteredScenarios = allScenarios.filter((scenario: ScenarioGalleryItem) => {
    // ScenarioList uses 'name', ScenarioDetail uses 'title'. 
    // Assuming 'name' is correct for classesData scenarios.
    const scenarioName = scenario.name || scenario.title || '';
    const matchesSearch = scenarioName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (scenario.includes && scenario.includes.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesClass = classFilter === 'all' || scenario.classId === classFilter;

    // Category tab filtering (based on classType)
    let matchesCategory = true;
    if (categoryTab !== 'all') {
      matchesCategory = scenario.classType === categoryTab;
    }

    let matchesPlatform = true;
    if (platformFilter !== 'all') {
      if (platformFilter === 'vr') {
        matchesPlatform = scenario.platform === 'VR';
      } else if (platformFilter === 'mobile') {
        matchesPlatform = scenario.platform === 'Mobile';
      } else if (platformFilter === 'pc') {
        matchesPlatform = scenario.platform === 'PC';
      }
    }

    return matchesSearch && matchesClass && matchesCategory && matchesPlatform;
  });

  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredScenarios.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentScenarios = filteredScenarios.slice(startIndex, endIndex);

  // 페이지네이션 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPlatformText = (platform: string | string[] | undefined): string | string[] => {
    if (!platform) return '';

    // 시나리오 플랫폼: VR | Mobile | PC 중 1개
    const platformMap: { [key: string]: string } = {
      'VR': 'HMD',
      'Mobile': 'Mobile',
      'PC': 'PC'
    };

    if (Array.isArray(platform)) {
      return platform;
    }

    return platformMap[platform] || platform;
  };

  return (
    <>
      <PageHeader
        title={t('list.title')}
        breadcrumbs={[{ label: t('list.title') }]}
        actions={
          <Link to={ROUTES.COMMON.CLASS_LIST}>
            <Button variant="outline" size="md">
              {t('list.subscribe')}
            </Button>
          </Link>
        }
      />

      {/* 설명 영역 */}
      <p className="text-base text-gray-600 mb-8">
        {t('list.description')}
      </p>

      {isError && !isLoading && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="mb-2">
            {error instanceof Error ? error.message : t('list.loadError')}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
            {t('list.retry')}
          </Button>
        </div>
      )}

      {/* 탭 메뉴 */}
      <div className="mb-8">
        <Tabs
          value={categoryTab}
          onValueChange={setCategoryTab}
        >
          <TabsList>
            <TabsTrigger value="all">{t('list.tabAll')}</TabsTrigger>
            <TabsTrigger value="ls">{t('list.tabSkills')}</TabsTrigger>
            <TabsTrigger value="ao">{t('list.tabDisease')}</TabsTrigger>
            <TabsTrigger value="md">{t('list.tabDiagnosis')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* List Header with Total and Filters */}
      <ListHeader
        totalCount={filteredScenarios.length}
        rightContent={
          <>
            <FilterGroup>
              <FilterSelect
                value={platformFilter}
                onValueChange={(val) => setPlatformFilter(val)}
                options={[
                  { value: 'all', label: t('list.filterAllPlatforms') },
                  { value: 'vr', label: 'VR' },
                  { value: 'mobile', label: 'Mobile' },
                  { value: 'pc', label: 'PC' }
                ]}
              />
            </FilterGroup>
            <SearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('list.searchPlaceholder')}
            />
          </>
        }
      />

      {/* 시나리오 갤러리 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
        {isLoading ? (
          <p className="col-span-full text-sm text-gray-500 py-12 text-center" role="status">
            {t('list.loading')}
          </p>
        ) : filteredScenarios.length > 0 ? (
          currentScenarios.map((scenario) => {
            let platforms: string[] = [];
            if (Array.isArray(scenario.platform)) {
              // If it is already an array, map each item
              platforms = (scenario.platform as string[])
                .map(p => {
                  const text = getPlatformText(p);
                  return Array.isArray(text) ? text : (text ? [text] : []);
                })
                .flat()
                .filter(Boolean) as string[];
            } else if (scenario.platform) {
              const text = getPlatformText(scenario.platform);
              platforms = Array.isArray(text) ? text : (text ? [text] : []);
            }

            return (
              <GalleryCard
                key={`${scenario.classId}-${scenario.id}`}
                to={`/scenario/${scenario.id}`}
                title={scenario.name || scenario.title || 'Untitled'}
                duration={scenario.duration}
                platforms={platforms}
                isNew={scenario.isNew}
              />
            );
          })
        ) : (
          <EmptyState
            icon={<Search size={48} />}
            title={isError ? t('list.loadError') : t('list.emptyTitle')}
            description={isError ? (error instanceof Error ? error.message : '') : t('list.emptyDescription')}
            className="col-span-full"
          />
        )}
      </div>

      {/* Pagination */}
      {!isLoading && (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredScenarios.length}
        onPageChange={handlePageChange}
        itemsPerPage={itemsPerPage}
      />
      )}
    </>
  );
}
